/**
 * Product line as the API serves it, both in the product lines referential and
 * inside the application configuration (`GET config`), which is why the schema
 * lives here rather than in the feature.
 */
import { z } from 'zod';
import { KEYBOARDS, type KeyboardKey } from '@/shared/constants';

const keyboardSchema = z.object({
  active: z.boolean().nullish(),
  maxChars: z.union([z.number(), z.string()]).nullish(),
  font: z.string().nullish(),
  fontWeight: z.string().nullish(),
});

export type KeyboardSettings = z.infer<typeof keyboardSchema>;

/** The `keyboard` object of a product line: one entry per keyboard plus the option maps. */
export const productLineKeyboardSchema = z.object({
  latin: keyboardSchema.nullish(),
  chinese: keyboardSchema.nullish(),
  chineseTw: keyboardSchema.nullish(),
  chineseHk: keyboardSchema.nullish(),
  arabic: keyboardSchema.nullish(),
  cyrillic: keyboardSchema.nullish(),
  specialChars: z.record(z.string(), z.boolean()).nullish(),
  allowedCases: z.record(z.string(), z.boolean()).nullish(),
  allowedIconicSymbols: z.record(z.string(), z.boolean()).nullish(),
  allowedExclusiveSymbols: z.record(z.string(), z.boolean()).nullish(),
});

export type ProductLineKeyboard = z.infer<typeof productLineKeyboardSchema>;

const offsetSchema = z.object({
  scale: z.number().nullish(),
  offsetX: z.number().nullish(),
  offsetY: z.number().nullish(),
});

export const productLineSchema = z.object({
  id: z.string(),
  productLine: z.string().nullish(),
  commercialProductLine: z.string().nullish(),
  commercialProductLineTranslations: z.record(z.string(), z.string()).nullish(),
  display: z.union([z.number(), z.string()]).nullish(),
  productGroup: z.string().nullish(),
  sav: z.boolean().nullish(),
  shadowType: z.string().nullish(),
  sizeDisplay: z.string().nullish(),
  keyboard: productLineKeyboardSchema.nullish(),
  stores: z.array(z.string()).nullish(),
  workshopId: z.string().nullish(),
  textProps: z
    .object({
      rotate: z.number().nullish(),
      curve: z.number().nullish(),
      letterSpace: z.number().nullish(),
    })

    .extend(offsetSchema.shape)
    .nullish(),
  imageProps: z.object({ custom: offsetSchema.nullish() }).nullish(),
});

export type ProductLine = z.infer<typeof productLineSchema>;

/** Whether a string names one of the keyboards of a product line. */
export const isKeyboardKey = (value: string): value is KeyboardKey =>
  value in KEYBOARDS;

/** The settings of one keyboard of a product line, by name. */
export const keyboardSettingsOf = (
  keyboard: ProductLineKeyboard | null | undefined,
  name: string,
): KeyboardSettings | null | undefined =>
  keyboard && isKeyboardKey(name) ? keyboard[name] : undefined;
